import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CloudArrowUp, HardDrives, CheckCircle, XCircle, Clock, ArrowClockwise, Plug, LinkBreak } from "@phosphor-icons/react";

const fmtSize = (b) => {
  if (!b) return "—";
  const mb = b / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(b / 1024).toFixed(1)} KB`;
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const StatusBadge = ({ s }) => {
  const map = {
    Successful: "bg-emerald-100 text-emerald-700",
    Verified: "bg-emerald-100 text-emerald-700",
    Failed: "bg-red-100 text-red-700",
    "In Progress": "bg-amber-100 text-amber-700",
    Pending: "bg-zinc-100 text-zinc-600",
  };
  return <Badge className={`rounded-sm font-normal ${map[s] || "bg-zinc-100 text-zinc-600"}`}>{s}</Badge>;
};

export default function BackupSettings() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingTime, setSavingTime] = useState(false);
  const [time, setTime] = useState("02:00");
  const poll = useRef(null);

  const loadStatus = useCallback(async () => {
    try { const r = await api.get("/backup/status"); setStatus(r.data); setTime(r.data.backup_time || "02:00"); }
    catch { /* admin only */ }
  }, []);
  const loadHistory = useCallback(async () => {
    try { setHistory((await api.get("/backup/history")).data || []); } catch { /* */ }
  }, []);

  useEffect(() => { loadStatus(); loadHistory(); return () => poll.current && clearInterval(poll.current); }, [loadStatus, loadHistory]);

  const connect = async () => {
    try { const r = await api.get("/oauth/drive/connect"); window.location.href = r.data.authorization_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Google connection"); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? Automatic backups will stop until reconnected.")) return;
    try { await api.post("/backup/disconnect"); toast.success("Google Drive disconnected"); loadStatus(); }
    catch { toast.error("Failed to disconnect"); }
  };
  const toggleAuto = async (val) => {
    setStatus((s) => ({ ...s, auto_enabled: val }));
    try { await api.put("/backup/settings", { auto_enabled: val }); toast.success(`Automatic backup ${val ? "ON" : "OFF"}`); }
    catch { toast.error("Failed to update"); loadStatus(); }
  };
  const saveTime = async () => {
    setSavingTime(true);
    try { await api.put("/backup/settings", { backup_time: time }); toast.success("Backup time saved"); loadStatus(); }
    catch { toast.error("Failed to save time"); } finally { setSavingTime(false); }
  };
  const backupNow = async () => {
    setBusy(true);
    try {
      await api.post("/backup/now");
      toast.info("Backup started — uploading to Google Drive…");
      loadHistory();
      poll.current && clearInterval(poll.current);
      poll.current = setInterval(async () => {
        const r = await api.get("/backup/history");
        setHistory(r.data || []);
        const top = (r.data || [])[0];
        if (top && top.status !== "In Progress") {
          clearInterval(poll.current); setBusy(false); loadStatus();
          top.status === "Successful" ? toast.success("Backup uploaded & verified on Google Drive")
            : toast.error(`Backup failed at: ${top.failed_stage || "unknown"}`);
        }
      }, 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start backup"); setBusy(false); }
  };

  if (!status) return null;
  const connected = status.connected;

  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-6 lg:col-span-2" data-testid="backup-settings-card">
      <div className="flex items-center gap-2 mb-5 text-zinc-900"><CloudArrowUp size={20} weight="duotone" /><h3 className="font-heading text-base">Backup Settings</h3></div>

      {/* Google Drive */}
      <div className="rounded-sm border border-zinc-200 p-4 mb-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-sm bg-brand/10 text-brand"><HardDrives size={22} weight="duotone" /></div>
            <div>
              <div className="font-medium text-zinc-900 flex items-center gap-2">
                Google Drive
                {connected
                  ? <Badge className="rounded-sm bg-emerald-100 text-emerald-700 font-normal" data-testid="drive-status">CONNECTED</Badge>
                  : <Badge className="rounded-sm bg-zinc-100 text-zinc-600 font-normal" data-testid="drive-status">NOT CONNECTED</Badge>}
              </div>
              <div className="text-xs text-zinc-500">
                {connected ? <>Account: {status.account_email || "connected"} · Folder: {status.backup_folder}</>
                  : status.oauth_configured ? "Connect your Google account to enable automatic cloud backups."
                    : "Server not configured yet — add Google OAuth credentials to enable this."}
              </div>
            </div>
          </div>
          {connected
            ? <Button variant="outline" size="sm" className="rounded-sm border-red-200 text-red-600 hover:bg-red-50" onClick={disconnect} data-testid="drive-disconnect"><LinkBreak size={15} className="mr-1.5" /> Disconnect</Button>
            : <Button size="sm" className="rounded-sm bg-brand hover:bg-brand-dark" onClick={connect} disabled={!status.oauth_configured} data-testid="drive-connect"><Plug size={15} className="mr-1.5" /> Connect Google Drive</Button>}
        </div>
      </div>

      {/* Auto backup controls */}
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <div className="rounded-sm border border-zinc-200 p-4 flex items-center justify-between">
          <div><div className="text-sm text-zinc-900 font-medium">Automatic Backup</div><div className="text-xs text-zinc-500">Runs daily at the set time</div></div>
          <Switch checked={!!status.auto_enabled} onCheckedChange={toggleAuto} data-testid="auto-backup-switch" />
        </div>
        <div className="rounded-sm border border-zinc-200 p-4">
          <Label className="text-xs text-zinc-500 mb-1.5 block">Backup Time (IST)</Label>
          <div className="flex items-center gap-2">
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-9 rounded-sm" data-testid="backup-time-input" />
            <Button size="sm" variant="outline" className="rounded-sm" onClick={saveTime} disabled={savingTime} data-testid="backup-time-save">Save</Button>
          </div>
        </div>
        <div className="rounded-sm border border-zinc-200 p-4 flex flex-col justify-between">
          <div className="text-xs text-zinc-500">Run a backup right now</div>
          <Button className="rounded-sm bg-brand hover:bg-brand-dark mt-2" onClick={backupNow} disabled={busy || !connected} data-testid="backup-now-btn">
            {busy ? <><ArrowClockwise size={15} className="mr-1.5 animate-spin" /> Backing up…</> : <><CloudArrowUp size={15} className="mr-1.5" /> Backup Now</>}
          </Button>
        </div>
      </div>

      {/* Last backup summary */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Info icon={<CheckCircle size={16} className="text-emerald-500" />} label="Last Successful" value={fmtDate(status.last_success_at)} sub={fmtSize(status.last_success_size)} testid="last-success" />
        <Info icon={<XCircle size={16} className="text-red-500" />} label="Last Failed" value={fmtDate(status.last_failed_at)} sub={status.last_failed_error} testid="last-failed" />
        <Info icon={<Clock size={16} className="text-zinc-400" />} label="Schedule" value={`Daily ${status.backup_time} IST`} sub={status.auto_enabled ? "Automatic: ON" : "Automatic: OFF"} testid="schedule-info" />
      </div>

      {/* History */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-zinc-400">Backup History</div>
          <Button variant="ghost" size="sm" className="rounded-sm text-zinc-500 h-7" onClick={loadHistory}><ArrowClockwise size={14} className="mr-1" /> Refresh</Button>
        </div>
        <div className="overflow-x-auto border border-zinc-200 rounded-sm">
          <table className="w-full text-sm" data-testid="backup-history-table">
            <thead><tr className="bg-zinc-50 text-left text-zinc-500">
              {["Date & Time", "Backup ID", "Size", "Type", "Status", "Google Drive"].map((h) => <th key={h} className="px-3 py-2 font-medium text-xs">{h}</th>)}
            </tr></thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-zinc-400">No backups yet.</td></tr>
              ) : history.map((h) => (
                <tr key={h.backup_id} className="border-t border-zinc-100">
                  <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{fmtDate(h.created_at)}</td>
                  <td className="px-3 py-2 text-zinc-500 font-mono text-xs">{h.backup_id}</td>
                  <td className="px-3 py-2 text-zinc-600">{fmtSize(h.size_bytes)}</td>
                  <td className="px-3 py-2 text-zinc-600">{h.type}{h.monthly ? " · Monthly" : ""}</td>
                  <td className="px-3 py-2"><StatusBadge s={h.status} />{h.status === "Failed" && h.failed_stage ? <div className="text-[11px] text-red-500 mt-0.5">{h.failed_stage}</div> : null}</td>
                  <td className="px-3 py-2"><StatusBadge s={h.drive_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Info = ({ icon, label, value, sub, testid }) => (
  <div className="rounded-sm border border-zinc-200 p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1">{icon} {label}</div>
    <div className="text-sm text-zinc-900">{value}</div>
    {sub ? <div className="text-[11px] text-zinc-400 mt-0.5 truncate">{sub}</div> : null}
  </div>
);
